import {omit} from 'es-toolkit';

import {getFirstFulfilledInSequence} from '@yourcoach/async-utils';

import type {
  CoreClientProgramsReadParams,
  CoreCoachProgramsReadParams,
  CorePublicProgramsReadParams,
} from '../../../api/types/core/methods';
import type {AsyncStrategy, BaseConfig, Batch} from '../utils';

import {programStore} from '../../../api/program';
import {isIamCoach} from '../../../utils/entity/isIamCoach';

export {
  programFetch,
  /** @public */
  programFetchBatch,
};

type Config = BaseConfig<
  | CoreClientProgramsReadParams
  | CorePublicProgramsReadParams
  | (Omit<CoreCoachProgramsReadParams, 'author_id'> & {coach_id?: string})
>;

const strategy: AsyncStrategy = async (roles, config) => {
  return isIamCoach()
    ? getFirstFulfilledInSequence([roles.coach, roles.client], config)
    : roles.client(config);
};

async function programFetch(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

async function programFetchBatch(config: Batch<Config>) {
  return strategy({client: asClientBatch, coach: asCoachBatch}, config);
}

programFetch.asClient = asClient;
programFetch.asCoach = asCoach;

async function asClient(config: Config) {
  const validParams = mapParams(config.params);

  return programStore.client.fetch(validParams);
}

async function asClientBatch(config: Batch<Config>) {
  const validParams = config.params.map(mapParams);

  return programStore.client.fetchBatch(validParams);
}

async function asCoach(config: Config) {
  const validParams = mapParams(config.params);

  return programStore.coach.fetch(validParams);
}

async function asCoachBatch(config: Batch<Config>) {
  const validParams = config.params.map(mapParams);

  return programStore.fetchBatch(validParams);
}

function mapParams<P extends CoreClientProgramsReadParams>(
  p: P,
): CoreCoachProgramsReadParams {
  return {
    ...omit(p, ['coach_id']),
  };
}
